import { AlertTriangle, Info } from "lucide-react";
import type * as React from "react";
import { cn } from "../../lib/cn";

/**
 * AlertBanner — DRY status banner for surfaces.
 *
 * Phase 4.2 consolidation (June 11, 2026): four sections of the
 * project track each spelled out the same ``flex items-center gap-2
 * rounded-md border border-error/30 bg-error/5 …`` markup with a
 * ``Loader2`` glyph (visually MISLEADING — code review C1 flagged it).
 * This primitive owns the brand-correct chrome + the correct semantic
 * icon per tone (AlertTriangle for ``error`` / ``warning``, Info for
 * ``info``) and sets ``role="alert"`` for screen readers.
 *
 * Tones:
 *   - ``error``   — used when a query fails. Red palette + ``role=alert``.
 *   - ``warning`` — used for non-blocking caveats (e.g. GDPR hints,
 *                   approaching limits). Amber palette + ``role=status``.
 *   - ``info``    — quiet inline notice (e.g. explainer banner over a
 *                   timeline). Neutral ink-3 palette + ``role=status``.
 *
 * Layout: icon + free-form children (string or JSX). The primitive
 * stays purely presentational — never owns query state.
 */
export type AlertBannerTone = "error" | "warning" | "info";

export interface AlertBannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "role" | "children"> {
  /** Visual + semantic tone. Default ``info``. */
  tone?: AlertBannerTone;
  /** Override the default icon (AlertTriangle for error/warning, Info for info). */
  icon?: React.ReactNode;
  /** Banner body — string or JSX (e.g. <Trans/>). */
  children: React.ReactNode;
}

const TONE_CLASS: Record<AlertBannerTone, string> = {
  error: "border-error/30 bg-error/5 text-error",
  warning: "border-warning/30 bg-warning/5 text-warning",
  info: "border-hairline bg-ink-1/[0.02] text-ink-3",
};

const TONE_ROLE: Record<AlertBannerTone, "alert" | "status"> = {
  error: "alert",
  warning: "status",
  info: "status",
};

const TONE_DEFAULT_ICON: Record<AlertBannerTone, React.ReactNode> = {
  error: <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />,
  warning: <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />,
  info: <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />,
};

export const AlertBanner: React.FC<AlertBannerProps> = ({
  tone = "info",
  icon,
  children,
  className,
  ...rest
}) => (
  <div
    role={TONE_ROLE[tone]}
    className={cn(
      "flex items-start gap-2 rounded-md border px-3 py-2.5 text-[12px] leading-relaxed",
      TONE_CLASS[tone],
      className,
    )}
    {...rest}
  >
    {icon ?? TONE_DEFAULT_ICON[tone]}
    <span className="min-w-0 flex-1">{children}</span>
  </div>
);

AlertBanner.displayName = "AlertBanner";
